import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Building2, BookOpen, Plus, Search, X, Edit2, Check, Trash2, Layers, BookText, Package } from 'lucide-react'; /* [PATCH izdaja-artikli v1] Package */
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { api } from '../lib/api';
import { useAuthStore } from '../stores/authStore';
import { smerAplikacije, preberiAplikacijo } from '../aplikacije'; // [PATCH aplikacije-fe v3] smer po aktivni aplikaciji
import { PartnerFursModal } from '../components/PartnerFursForm'; // [PATCH_ROCNI_UVOZ_PARTNER_V1]
import { SifrantArtiklov, useIzdajaModul } from '../components/sifranti/SifrantArtiklov'; /* [PATCH izdaja-artikli v1] */
import { izbiraNacin, izbiraPrekini, izbiraVrni } from '../lib/sifrantIzbira'; // [PATCH izdaja-prodajni-dok-ui v3]

type Tab = 'partnerji' | 'kontni_plan' | 'sm' | 'projekti' | 'vrste_knjige' | 'keywords' | 'artikli'; // [PATCH projekti-sifrant v1]

const inputCls = 'w-full px-2 py-1 text-xs bg-gray-800 border border-blue-500 rounded outline-none text-white';
const addBtnCls = 'flex items-center gap-1 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm transition';
const cardCls = 'bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 overflow-hidden';
const thCls = 'px-4 py-3 text-left text-xs text-gray-500 font-medium';

const DDV_OPTS: { v: string; l: string }[] = [
  { v: '', l: 'Ni podatka' },
  { v: 'ne', l: 'Ne' },
  { v: 'da', l: 'Da' },
  { v: 'eu', l: 'Izvoz ali EU' },
  { v: 'mali', l: 'Mali davčni zavezanec' },
];
const ddvLabel = (c: string) => DDV_OPTS.find(o => o.v === (c || ''))?.l || 'Ni podatka';

// PATCH_SIFRANTI_INSOLV_ZNACKE_V1: iste oznake/barve kot na strani Insolventnost
const normDav8 = (v: any) => String(v || '').replace(/\D/g, '').slice(-8);
const insolvKlas = (v: any) => {
  const t = String(v || '').toLowerCase();
  if (t.includes('likvidacij'))
    return { label: 'Likvidacija', cls: 'bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-300' };
  if (t.includes('poravnav'))
    return { label: 'Prisilna poravnava', cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300' };
  if (t.includes('zapu'))
    return { label: 'Stečaj zapuščine', cls: 'bg-violet-100 text-violet-700 dark:bg-violet-900 dark:text-violet-300' };
  if (t.includes('osebn'))
    return { label: 'Osebni stečaj', cls: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300' };
  if (t.includes('prestrukt'))
    return { label: 'Prestrukturiranje', cls: 'bg-teal-100 text-teal-700 dark:bg-teal-900 dark:text-teal-300' };
  if (t.includes('stečaj') || t.includes('stecaj'))
    return { label: 'Stečaj', cls: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300' };
  return { label: v || 'V postopku', cls: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300' };
};


export function SifrantiPage() {
  const { selectedCompany } = useAuthStore();
  const companyId = selectedCompany?.id || '';
  const [tab, setTab] = useState<Tab>((new URLSearchParams(window.location.search).get('tab') as Tab) || 'partnerji'); // [PATCH izdaja-prodajni-dok-ui v3] ?tab=
  const stranka = (useAuthStore() as any).role === 'stranka'; // [PATCH izdaja-prodajni-dok-ui v3] stranka: samo partnerji, SM, projekti, artikli
  const izbiraSp = (() => { const s = new URLSearchParams(window.location.search); return s.get('izbira') ? s : null; })(); // [PATCH izdaja-prodajni-dok-ui v3]
  const izdajaModul = useIzdajaModul(companyId); /* [PATCH izdaja-artikli v1] zavihek samo pri vklopljenem modulu */

  const tabsVsi: { id: Tab; label: string; icon: any }[] = [ // [PATCH izdaja-prodajni-dok-ui v3] vsi; tabs = filtrirani po vlogi
    { id: 'partnerji', label: 'Partnerji', icon: Building2 },
    { id: 'kontni_plan', label: 'Kontni plan', icon: BookOpen },
    { id: 'sm', label: 'Stroškovna mesta', icon: Layers },
    { id: 'projekti', label: 'Projekti', icon: Layers }, // [PATCH projekti-sifrant v1]
    { id: 'keywords', label: 'Opisi za konte', icon: BookText },
    ...(smerAplikacije(preberiAplikacijo()) === 'izdani_racun' ? [] : [{ id: 'vrste_knjige' as Tab, label: 'Vrste knjige DDV', icon: BookText }]), /* [PATCH aplikacije-fe v3] samo v Nabavi */
    ...(izdajaModul ? [{ id: 'artikli' as Tab, label: 'Artikli', icon: Package }] : []), /* [PATCH izdaja-artikli v1] */
  ];
  const tabs = tabsVsi.filter(t => !stranka || (['partnerji', 'sm', 'projekti', 'artikli'] as Tab[]).includes(t.id)); // [PATCH izdaja-prodajni-dok-ui v3]

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Šifranti</h1>
        <p className="text-gray-500 dark:text-gray-400 mt-1">Partnerji, kontni plan, stroškovna mesta in vrste knjige DDV</p>
        {izbiraSp && (
          <div className="mt-3 flex items-center gap-3 rounded-xl border border-blue-300 bg-blue-50 dark:bg-blue-500/10 dark:border-blue-700 px-4 py-2 text-sm">
            <span><b>Izbira za dokument:</b> klikni vrstico, ki jo želiš prenesti v masko.</span>
            <button className="ml-auto text-xs underline" onClick={() => izbiraPrekini(izbiraSp)}>nazaj brez izbire</button>
          </div>
        )}{/* [PATCH izdaja-prodajni-dok-ui v3] */}
      </div>

      <div className="flex gap-2 flex-wrap">
        {tabs.map(t => {
          const Icon = t.icon;
          return (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={clsx('flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium border transition',
                tab === t.id ? 'bg-blue-600 text-white border-blue-600' : 'bg-white dark:bg-gray-900 text-gray-600 dark:text-gray-400 border-gray-200 dark:border-gray-700 hover:border-blue-400')}>
              <Icon className="w-4 h-4" />{t.label}
            </button>
          );
        })}
      </div>

      {!companyId && <div className="text-center py-20 text-gray-400">Izberite podjetje v glavi zgoraj.</div>}
      {companyId && tab === 'partnerji' && <SifrantPartnerjev key={companyId} companyId={companyId} />}
      {companyId && tab === 'kontni_plan' && <SifrantKontov key={companyId} companyId={companyId} />}
      {companyId && tab === 'sm' && <SifrantSM key={companyId} companyId={companyId} />}
      {companyId && tab === 'projekti' && <SifrantProjekti key={companyId} companyId={companyId} />}{/* [PATCH projekti-sifrant v1] */}
      {companyId && tab === 'keywords' && <SifrantKeywords key={companyId} companyId={companyId} />}
      {companyId && tab === 'vrste_knjige' && <SifrantVrsteKnjige key={companyId} companyId={companyId} />}
      {companyId && tab === 'artikli' && /* [PATCH izdaja-artikli v1] */ <SifrantArtiklov key={companyId} companyId={companyId} />}
    </div>
  );
}

/* ============================ PARTNERJI ============================ */
function SifrantPartnerjev({ companyId }: { companyId: string }) {
  const [partnerji, setPartnerji] = useState<any[]>([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [modalInitial, setModalInitial] = useState<any | null>(null);

  // PATCH_SIFRANTI_PAGINACIJA_FE_V1
  const [stran, setStran] = useState(1);
  const [strani, setStrani] = useState(1);
  const [skupaj, setSkupaj] = useState(0);
  const [sort, setSort] = useState('vasco');
  const [smer, setSmer] = useState<'asc' | 'desc'>('asc');
  const [qDeb, setQDeb] = useState('');

  const nalozi = (query: string, st: number, so: string, sm: string) => {
    setLoading(true);
    const url = `/sifranti/partnerji/${companyId}/stran?na_stran=200&stran=${st}&sort=${so}&smer=${sm}`
      + (query.trim() ? `&q=${encodeURIComponent(query.trim())}` : '');
    api.get(url).then(r => {
      setPartnerji(r.data.items || []);
      setSkupaj(r.data.skupaj || 0);
      setStrani(r.data.strani || 1);
      setLoading(false);
    }).catch(() => { setPartnerji([]); setSkupaj(0); setStrani(1); setLoading(false); });
  };

  // debounce iskanja + reset na prvo stran
  useEffect(() => {
    const t = setTimeout(() => { setQDeb(q); setStran(1); }, 300);
    return () => clearTimeout(t);
  }, [q]);
  useEffect(() => { nalozi(qDeb, stran, sort, smer); }, [qDeb, stran, sort, smer]);

  const klikSort = (polje: string) => {
    if (sort === polje) setSmer(s => (s === 'asc' ? 'desc' : 'asc'));
    else { setSort(polje); setSmer('asc'); }
    setStran(1);
  };
  const sortInd = (polje: string) => (sort === polje ? (smer === 'asc' ? ' \u25b2' : ' \u25bc') : '');

  // PATCH_SIFRANTI_INSOLV_ZNACKE_V1: mapa insolventnih partnerjev te baze
  const [insolvMap, setInsolvMap] = useState<Map<string, string>>(new Map());
  useEffect(() => {
    let zivo = true;
    api.get('/insolventnost', { params: { obseg: 'moji', company_id: companyId } })
      .then((r: any) => {
        if (!zivo) return;
        const m = new Map<string, string>();
        (r.data?.postavke || []).forEach((d: any) => {
          const k = normDav8(d.davcna);
          if (k && !m.has(k)) m.set(k, d.vrsta_postopka || '');
        });
        setInsolvMap(m);
      })
      .catch(() => {});
    return () => { zivo = false; };
  }, [companyId]);
  const insolvZnacka = (p: any) => {
    const k = normDav8(p.tax_number) || normDav8(p.vat_number);
    return k && insolvMap.has(k) ? insolvKlas(insolvMap.get(k)) : null;
  };

  const izbrisi = async (p: any) => {
    // [PATCH partner-brisanje-odvisnosti v1] najprej odvisnosti, nato ustrezna potrditev
    let odv: any = null;
    try {
      const r = await api.get(`/sifranti/partnerji/${companyId}/${p.id}/odvisnosti`);
      odv = r.data;
    } catch (e: any) {
      toast.error(e.response?.data?.detail || 'Odvisnosti ni bilo mogoče preveriti');
      return;
    }
    const dok = odv?.dokumenti || [];
    if (!odv?.lahko_brisati) {
      const vrstice = dok.slice(0, 12).map((d: any) =>
        `• ${d.interno_st ? d.interno_st + ' — ' : ''}${d.doc_number || '—'}  (${d.doc_type}, ${d.datum || '—'}, ${d.znesek} EUR)`
      ).join('\n');
      const vec = dok.length > 12 ? `\n… in še ${(odv.st_dokumentov || dok.length) - 12}` : '';
      window.alert(
        `Partnerja "${p.name}" ni mogoče izbrisati.\n\n` +
        `Vezan je na ${odv?.st_dokumentov ?? dok.length} dokument(ov):\n\n${vrstice}${vec}\n\n` +
        `Najprej dokumente prevežite na drugega partnerja ali jih izbrišite.`
      );
      return;
    }
    let potrdiVzorce = false;
    const vz = odv?.vzorci || 0;
    if (vz > 0) {
      if (!window.confirm(
        `Partner "${p.name}" ni vezan na nobenega dokumenta, ima pa ${vz} učnih vzorcev za kontiranje.\n\n` +
        `Ob izbrisu partnerja bodo ti vzorci trajno izbrisani.\n\nNadaljujem?`
      )) return;
      potrdiVzorce = true;
    } else {
      if (!window.confirm(`Dokončno izbrišem partnerja "${p.name}"?`)) return;
    }
    try {
      await api.delete(`/sifranti/partnerji/${companyId}/${p.id}` + (potrdiVzorce ? '?potrdi_vzorce=true' : ''));
      toast.success(potrdiVzorce ? `Izbrisano (tudi ${vz} vzorcev)` : 'Izbrisano');
      setPartnerji(prev => prev.filter(x => x.id !== p.id));
      setSkupaj(s => Math.max(0, s - 1));
    } catch (e: any) { toast.error(e.response?.data?.detail || 'Napaka'); }
  };

  const posta = (p: any) => [p.postal_code, p.city].filter(Boolean).join(' ') || '—';
  const ibaniOf = (p: any) => (Array.isArray(p.ibani) ? p.ibani : []);
  const primIban = (p: any) => {
    const a = ibaniOf(p);
    const pr = a.find((x: any) => x.primarni) || a[0];
    return pr ? pr.iban : '';
  };

  return (
    <div className={cardCls}>
      <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 flex-1 px-3 py-2 bg-gray-50 dark:bg-gray-800 rounded-xl">
          <Search className="w-4 h-4 text-gray-400" />
          <input value={q} onChange={e => setQ(e.target.value)}
            className="bg-transparent text-sm text-gray-900 dark:text-white outline-none flex-1"
            placeholder="Išči po imenu, davčni, ID za DDV..." />
          {q && <button onClick={() => setQ('')}><X className="w-4 h-4 text-gray-400" /></button>}
        </div>
        <span className="text-xs text-gray-400 whitespace-nowrap">{skupaj} skupaj</span>
        <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
          <button onClick={() => setStran(s => Math.max(1, s - 1))} disabled={stran <= 1}
            className="px-2 py-0.5 rounded-lg border border-gray-200 dark:border-gray-700 disabled:opacity-30 hover:bg-gray-50 dark:hover:bg-gray-800">{'\u2039'}</button>
          <span>{stran} / {strani}</span>
          <button onClick={() => setStran(s => Math.min(strani, s + 1))} disabled={stran >= strani}
            className="px-2 py-0.5 rounded-lg border border-gray-200 dark:border-gray-700 disabled:opacity-30 hover:bg-gray-50 dark:hover:bg-gray-800">{'\u203a'}</button>
        </div>
        <button onClick={() => setModalInitial({})} className={addBtnCls}>
          <Plus className="w-4 h-4" />Dodaj
        </button>
      </div>

      <div className="overflow-x-auto max-h-[calc(100vh_-_340px)] overflow-y-auto">
        <table className="w-full text-sm whitespace-nowrap">
          <thead className="bg-gray-50 dark:bg-gray-800 sticky top-0">
            <tr>
              <th className={thCls + ' cursor-pointer select-none'} onClick={() => klikSort('vasco')}>Šifra{sortInd('vasco')}</th>
              <th className={thCls + ' cursor-pointer select-none'} onClick={() => klikSort('naziv')}>Naziv{sortInd('naziv')}</th>
              <th className={thCls}>Naslov</th>
              <th className={thCls + ' cursor-pointer select-none'} onClick={() => klikSort('kraj')}>Pošta{sortInd('kraj')}</th>
              <th className={thCls}>Drž.</th>
              <th className={thCls + ' cursor-pointer select-none'} onClick={() => klikSort('davcna')}>Davčna{sortInd('davcna')}</th>
              <th className={thCls}>Matična</th>{/* [PATCH sifrant-furs v1] */}
              <th className={thCls}>SKD</th>
              <th className={thCls}>Dav. zavezanec</th>
              <th className={thCls}>ID za DDV</th>
              <th className={thCls}>IBAN / TRR</th>
              <th className="px-4 py-3 text-center text-xs text-gray-500 font-medium">Uredi</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
            {loading ? (
              <tr><td colSpan={12} className="px-4 py-8 text-center text-gray-400 text-sm">Nalaganje...</td></tr>
            ) : partnerji.length === 0 ? (
              <tr><td colSpan={12} className="px-4 py-8 text-center text-gray-400 text-sm">Ni partnerjev za prikaz.</td></tr>
            ) : partnerji.map(p => {
              const ib = ibaniOf(p);
              const insolv = insolvZnacka(p);
              return (
                <tr key={p.id} className={clsx('hover:bg-gray-50 dark:hover:bg-gray-800 transition cursor-pointer', p.is_active === false && 'opacity-50')} onClick={() => { const s = izbiraNacin('partner'); if (s) izbiraVrni(s, 'partner', p); else setModalInitial(p); }}>{/* [PATCH izdaja-prodajni-dok-ui v3] */}{/* [PATCH sifranti-neaktivni v1] */}
                  <td className="px-4 py-2 font-mono text-xs text-gray-400">{p.vasco_code || '—'}{p.is_active === false && <span className="ml-1 rounded bg-gray-200 px-1 text-[10px] text-gray-600 dark:bg-gray-700 dark:text-gray-300" title="Partner je neaktiven — v izbirnikih dokumentov se ne ponuja">neaktiven</span>}</td>
                  <td className="px-4 py-2 font-medium text-gray-900 dark:text-white">
                    {p.name}
                    {insolv && <span className={'ml-2 px-2 py-0.5 rounded-full text-[11px] font-medium whitespace-nowrap ' + insolv.cls}>{insolv.label}</span>}
                  </td>
                  <td className="px-4 py-2 text-xs text-gray-500">{p.address || '—'}</td>
                  <td className="px-4 py-2 text-xs text-gray-500">{posta(p)}</td>
                  <td className="px-4 py-2 text-xs text-gray-500">{p.country || '—'}</td>
                  <td className="px-4 py-2 font-mono text-xs text-gray-500">{p.tax_number || '—'}</td>
                  <td className="px-4 py-2 font-mono text-xs text-gray-500">{p.registration_number || '—'}</td>{/* [PATCH sifrant-furs v1] */}
                  <td className="px-4 py-2 font-mono text-xs text-gray-500" title={p.furs?.financni_urad ? `FU: ${p.furs.financni_urad}` : undefined}>{p.skd || '—'}</td>
                  <td className="px-4 py-2 text-xs text-gray-500" title={p.furs?.datum_ddv ? `DDV od ${p.furs.datum_ddv}` : undefined}>
                    {p.ddv_status ? ddvLabel(p.ddv_status) : p.furs ? (p.furs.zavezanec_ddv ? <span className="text-green-500">✓ DDV</span> : <span className="text-gray-400">ni DDV</span>) : <span className="text-gray-300">—</span>}
                    {p.furs?.oznaka && <span className="ml-1 px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 text-[10px] font-semibold" title={p.furs.oznaka==='P' ? 'Ni plačnik po 76.a členu ZDDV-1' : 'Posebna ureditev — cestni prevoz'}>{p.furs.oznaka}</span>}
                    {p.je_pu && <span className="ml-1 px-1.5 py-0.5 rounded bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 text-[10px] font-semibold" title={`Proračunski uporabnik${p.pu_sifra ? ' — šifra '+p.pu_sifra : ''}${p.pu_tip==='A' ? ' (neposredni)' : ' (posredni)'}`}>{p.pu_tip==='A' ? 'PU-N' : 'PU'}</span>}{/* [PATCH pu-znacka v4] */}
                  </td>
                  <td className="px-4 py-2 font-mono text-xs text-gray-500">{p.vat_number || '—'}</td>
                  <td className="px-4 py-2 font-mono text-xs text-gray-500">
                    {ib.length === 0 ? '—' : (
                      <>{primIban(p)}{ib.length > 1 && <span className="text-blue-400"> +{ib.length - 1}</span>}</>
                    )}
                  </td>
                  <td className="px-4 py-2 text-center" onClick={e => e.stopPropagation()}>
                    <div className="flex justify-center gap-1">
                      <button onClick={() => setModalInitial(p)} className="p-1 text-gray-400 hover:text-blue-400 transition"><Edit2 className="w-4 h-4" /></button>
                      <button onClick={() => izbrisi(p)} className="p-1 text-gray-400 hover:text-red-400 transition"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {modalInitial && (
        <PartnerModal
          companyId={companyId}
          initial={modalInitial}
          onClose={() => setModalInitial(null)}
          onSaved={() => { setModalInitial(null); nalozi(qDeb, stran, sort, smer); }}
        />
      )}
    </div>
  );
}

/* ---------------------------- MODAL PARTNERJA ---------------------------- */
// [PATCH_ROCNI_UVOZ_PARTNER_V1] maska prestavljena v skupno komponento
// components/PartnerFursForm.tsx (isto masko uporablja rocni vnos v Dokumentih).
function PartnerModal({ companyId, initial, onClose, onSaved }: { companyId: string; initial: any; onClose: () => void; onSaved: () => void }) {
  return <PartnerFursModal companyId={companyId} initial={initial} onClose={onClose} onSaved={onSaved} />;
}

/* ============================ KONTNI PLAN ============================ */
function SifrantKontov({ companyId }: { companyId: string }) {
  const [konti, setKonti] = useState<any[]>([]);
  const [sm, setSm] = useState<any[]>([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [showDodaj, setShowDodaj] = useState(false);
  const [nov, setNov] = useState({ code: '', name: '' });
  const [editCode, setEditCode] = useState<string | null>(null);
  const [editData, setEditData] = useState<any>({});

  const nalozi = () => {
    setLoading(true);
    api.get(`/sifranti/kontni-plan/${companyId}?surovo=true`).then(r => { setKonti(r.data); setLoading(false); }).catch(() => { setKonti([]); setLoading(false); });
  };
  useEffect(() => {
    nalozi();
    api.get(`/sifranti/sm/${companyId}`).then(r => setSm(r.data)).catch(() => {});
  }, [companyId]);

  const filtered = useMemo(() => {
    const sq = q.toLowerCase();
    if (!sq) return konti.slice(0, 400);
    return konti.filter(k => String(k.code).startsWith(q) || (k.name || '').toLowerCase().includes(sq)).slice(0, 400);
  }, [q, konti]);

  const dodaj = async () => {
    if (!nov.code || !nov.name) { toast.error('Koda in naziv sta obvezna'); return; }
    try {
      await api.post(`/sifranti/kontni-plan/${companyId}/dodaj`, nov);
      toast.success('Konto dodan!');
      setNov({ code: '', name: '' });
      setShowDodaj(false);
      nalozi();
    } catch (e: any) { toast.error(e.response?.data?.detail || 'Napaka'); }
  };

  const shrani = async (code: string) => {
    try {
      await api.put(`/sifranti/kontni-plan/${companyId}/${code}`, editData);
      toast.success('Shranjeno!');
      setKonti(prev => prev.map(k => (k.code === code ? { ...k, ...editData } : k)));
      setEditCode(null);
    } catch (e: any) { toast.error(e.response?.data?.detail || 'Napaka'); }
  };

  const izbrisi = async (k: any) => {
    if (!window.confirm(`Izbrišem konto ${k.code} - ${k.name}?`)) return;
    try {
      await api.delete(`/sifranti/kontni-plan/${companyId}/${k.code}`);
      toast.success('Izbrisano');
      setKonti(prev => prev.filter(x => x.code !== k.code));
    } catch (e: any) { toast.error(e.response?.data?.detail || 'Napaka'); }
  };

  return (
    <div className={cardCls}>
      <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 flex-1 px-3 py-2 bg-gray-50 dark:bg-gray-800 rounded-xl">
          <Search className="w-4 h-4 text-gray-400" />
          <input value={q} onChange={e => setQ(e.target.value)}
            className="bg-transparent text-sm outline-none flex-1 text-gray-900 dark:text-white"
            placeholder="Išči po kontu ali nazivu..." />
          {q && <button onClick={() => setQ('')}><X className="w-4 h-4 text-gray-400" /></button>}
        </div>
        <span className="text-xs text-gray-400 whitespace-nowrap">{konti.length} kontov</span>
        <button onClick={() => setShowDodaj(!showDodaj)} className={addBtnCls}>
          <Plus className="w-4 h-4" />Dodaj
        </button>
      </div>

      {showDodaj && (
        <div className="px-4 py-4 border-b border-gray-200 dark:border-gray-800 bg-blue-50 dark:bg-blue-950 flex items-end gap-2">
          <div>
            <label className="text-xs text-gray-500 block mb-1">Koda *</label>
            <input value={nov.code} onChange={e => setNov(n => ({ ...n, code: e.target.value }))} placeholder="402000"
              className="w-32 px-2 py-1.5 text-xs bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg outline-none focus:border-blue-500" />
          </div>
          <div className="flex-1">
            <label className="text-xs text-gray-500 block mb-1">Naziv *</label>
            <input value={nov.name} onChange={e => setNov(n => ({ ...n, name: e.target.value }))} placeholder="Naziv konta"
              className="w-full px-2 py-1.5 text-xs bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg outline-none focus:border-blue-500" />
          </div>
          <button onClick={dodaj} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-xl">Shrani</button>
          <button onClick={() => setShowDodaj(false)} className="px-4 py-2 text-gray-500 hover:text-gray-700 text-sm">Prekliči</button>
        </div>
      )}

      <div className="overflow-x-auto max-h-[calc(100vh_-_340px)] overflow-y-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-800 sticky top-0">
            <tr>
              <th className={thCls + ' w-28'}>Koda</th>
              <th className={thCls}>Naziv</th>
              <th className="px-4 py-3 text-center text-xs text-gray-500 font-medium w-24">Zahteva SM</th>
              <th className={thCls + ' w-40'}>Privzeti SM</th>
              <th className="px-4 py-3 text-center text-xs text-gray-500 font-medium w-24">Saldakonto</th>{/* [PATCH saldakonto-sifrant v1] */}
              <th className="px-4 py-3 text-center text-xs text-gray-500 font-medium w-20">Uredi</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
            {loading ? (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-400 text-sm">Nalaganje...</td></tr>
            ) : filtered.map(k => {
              const knjizni = String(k.code).length >= 4;
              const ed = editCode === k.code;
              return (
                <tr key={k.code} className={clsx('hover:bg-gray-50 dark:hover:bg-gray-800 transition', !knjizni && 'opacity-50')}>
                  <td className={clsx('px-4 py-2 font-mono font-bold', knjizni ? 'text-blue-500' : 'text-gray-400')}>{k.code}</td>
                  <td className="px-4 py-2 text-gray-700 dark:text-gray-300">
                    {ed ? (
                      <input value={editData.name || ''} onChange={e => setEditData((d: any) => ({ ...d, name: e.target.value }))} className={inputCls} />
                    ) : k.name}
                  </td>
                  <td className="px-4 py-2 text-center">
                    {ed ? (
                      <input type="checkbox" checked={!!editData.requires_cost_center}
                        onChange={e => setEditData((d: any) => ({ ...d, requires_cost_center: e.target.checked, suggested_cost_center: e.target.checked ? d.suggested_cost_center : '' }))}
                        className="accent-blue-500" />
                    ) : (k.requires_cost_center ? <Check className="w-4 h-4 text-green-500 mx-auto" /> : <span className="text-gray-300">—</span>)}
                  </td>
                  <td className="px-4 py-2 text-xs text-gray-500">
                    {ed ? (
                      <select value={editData.suggested_cost_center || ''} disabled={!editData.requires_cost_center}
                        onChange={e => setEditData((d: any) => ({ ...d, suggested_cost_center: e.target.value }))}
                        className="w-full px-2 py-1 text-xs bg-gray-800 border border-blue-500 rounded outline-none text-white disabled:opacity-40">
                        <option value="">— brez —</option>
                        {sm.map(s => <option key={s.code} value={s.code}>{s.code} {s.name}</option>)}
                      </select>
                    ) : (k.suggested_cost_center ? <span className="font-mono">{k.suggested_cost_center}</span> : '—')}
                  </td>
                  <td className="px-4 py-2 text-center">
                    {ed ? (
                      <input type="checkbox" checked={!!editData.je_saldakonto}
                        onChange={e => setEditData((d: any) => ({ ...d, je_saldakonto: e.target.checked }))}
                        className="accent-blue-500" />
                    ) : (k.je_saldakonto ? <Check className="w-4 h-4 text-green-500 mx-auto" /> : <span className="text-gray-300">—</span>)}
                  </td>{/* [PATCH saldakonto-sifrant v1] partner je obvezen na tem kontu */}
                  <td className="px-4 py-2 text-center">
                    {ed ? (
                      <div className="flex justify-center gap-1">
                        <button onClick={() => shrani(k.code)} className="p-1 text-green-500 hover:text-green-400"><Check className="w-4 h-4" /></button>
                        <button onClick={() => setEditCode(null)} className="p-1 text-gray-400 hover:text-gray-300"><X className="w-4 h-4" /></button>
                      </div>
                    ) : (
                      <div className="flex justify-center gap-1">
                        <button onClick={() => { setEditCode(k.code); setEditData({ name: k.name, requires_cost_center: !!k.requires_cost_center, suggested_cost_center: k.suggested_cost_center || '', je_saldakonto: !!k.je_saldakonto /* [PATCH saldakonto-sifrant v1] */ }); }}
                          className="p-1 text-gray-400 hover:text-blue-400 transition"><Edit2 className="w-4 h-4" /></button>
                        <button onClick={() => izbrisi(k)} className="p-1 text-gray-400 hover:text-red-400 transition"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ============================ STROŠKOVNA MESTA ============================ */
function SifrantSM({ companyId }: { companyId: string }) {
  const [sm, setSm] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showDodaj, setShowDodaj] = useState(false);
  const [nov, setNov] = useState({ code: '', name: '' });
  const [editId, setEditId] = useState<string | null>(null);
  const [editData, setEditData] = useState<any>({});

  const nalozi = () => {
    setLoading(true);
    api.get(`/sifranti/sm/${companyId}`).then(r => { setSm(r.data); setLoading(false); }).catch(() => { setSm([]); setLoading(false); });
  };
  useEffect(() => { nalozi(); }, [companyId]);

  const dodaj = async () => {
    if (!nov.code || !nov.name) { toast.error('Šifra in naziv sta obvezna'); return; }
    try {
      await api.post(`/sifranti/sm/${companyId}`, nov);
      toast.success('Stroškovno mesto dodano!');
      setNov({ code: '', name: '' });
      setShowDodaj(false);
      nalozi();
    } catch (e: any) { toast.error(e.response?.data?.detail || 'Napaka'); }
  };

  const shrani = async (id: string) => {
    try {
      await api.put(`/sifranti/sm/${companyId}/${id}`, editData);
      toast.success('Shranjeno!');
      setSm(prev => prev.map(s => (s.id === id ? { ...s, ...editData } : s)));
      setEditId(null);
    } catch (e: any) { toast.error(e.response?.data?.detail || 'Napaka'); }
  };

  const izbrisi = async (s: any) => {
    if (!window.confirm(`Izbrišem stroškovno mesto ${s.code} - ${s.name}?`)) return;
    try {
      await api.delete(`/sifranti/sm/${companyId}/${s.id}`);
      toast.success('Izbrisano');
      setSm(prev => prev.filter(x => x.id !== s.id));
    } catch (e: any) { toast.error(e.response?.data?.detail || 'Napaka'); }
  };

  return (
    <div className={cardCls}>
      <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between gap-3">
        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Stroškovna mesta</span>
        <span className="text-xs text-gray-400 flex-1">{sm.length} mest</span>
        <button onClick={() => setShowDodaj(!showDodaj)} className={addBtnCls}>
          <Plus className="w-4 h-4" />Dodaj
        </button>
      </div>

      {showDodaj && (
        <div className="px-4 py-4 border-b border-gray-200 dark:border-gray-800 bg-blue-50 dark:bg-blue-950 flex items-end gap-2">
          <div>
            <label className="text-xs text-gray-500 block mb-1">Šifra *</label>
            <input value={nov.code} onChange={e => setNov(n => ({ ...n, code: e.target.value }))} placeholder="1"
              className="w-24 px-2 py-1.5 text-xs bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg outline-none focus:border-blue-500" />
          </div>
          <div className="flex-1">
            <label className="text-xs text-gray-500 block mb-1">Naziv *</label>
            <input value={nov.name} onChange={e => setNov(n => ({ ...n, name: e.target.value }))} placeholder="Naziv stroškovnega mesta"
              className="w-full px-2 py-1.5 text-xs bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg outline-none focus:border-blue-500" />
          </div>
          <button onClick={dodaj} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-xl">Shrani</button>
          <button onClick={() => setShowDodaj(false)} className="px-4 py-2 text-gray-500 hover:text-gray-700 text-sm">Prekliči</button>
        </div>
      )}

      <div className="overflow-x-auto max-h-[calc(100vh_-_340px)] overflow-y-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-800 sticky top-0">
            <tr>
              <th className={thCls + ' w-24'}>Šifra</th>
              <th className={thCls}>Naziv</th>
              <th className="px-4 py-3 text-center text-xs text-gray-500 font-medium w-20">Uredi</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
            {loading ? (
              <tr><td colSpan={3} className="px-4 py-8 text-center text-gray-400 text-sm">Nalaganje...</td></tr>
            ) : sm.length === 0 ? (
              <tr><td colSpan={3} className="px-4 py-8 text-center text-gray-400 text-sm">Ni stroškovnih mest. Dodajte prvo.</td></tr>
            ) : sm.map(s => (
              <tr key={s.id} className="hover:bg-gray-50 dark:hover:bg-gray-800 transition cursor-pointer" onClick={() => { const q = izbiraNacin('sm'); if (q && editId !== s.id) izbiraVrni(q, 'sm', s); }}>{/* [PATCH izdaja-prodajni-dok-ui v3] */}
                <td className="px-4 py-2 font-mono font-bold text-blue-500">
                  {editId === s.id ? (
                    <input value={editData.code || ''} onChange={e => setEditData((d: any) => ({ ...d, code: e.target.value }))} className="w-20 px-2 py-1 text-xs bg-gray-800 border border-blue-500 rounded outline-none text-white" />
                  ) : s.code}
                </td>
                <td className="px-4 py-2 text-gray-700 dark:text-gray-300">
                  {editId === s.id ? (
                    <input value={editData.name || ''} onChange={e => setEditData((d: any) => ({ ...d, name: e.target.value }))} className={inputCls} />
                  ) : s.name}
                </td>
                <td className="px-4 py-2 text-center">
                  {editId === s.id ? (
                    <div className="flex justify-center gap-1">
                      <button onClick={() => shrani(s.id)} className="p-1 text-green-500 hover:text-green-400"><Check className="w-4 h-4" /></button>
                      <button onClick={() => setEditId(null)} className="p-1 text-gray-400 hover:text-gray-300"><X className="w-4 h-4" /></button>
                    </div>
                  ) : (
                    <div className="flex justify-center gap-1">
                      <button onClick={() => { setEditId(s.id); setEditData({ code: s.code, name: s.name }); }}
                        className="p-1 text-gray-400 hover:text-blue-400 transition"><Edit2 className="w-4 h-4" /></button>
                      <button onClick={() => izbrisi(s)} className="p-1 text-gray-400 hover:text-red-400 transition"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}


/* ==================== PROJEKTI [PATCH projekti-sifrant v1] ==================== */
function SifrantProjekti({ companyId }: { companyId: string }) {
  const [pr, setPr] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showDodaj, setShowDodaj] = useState(false);
  const [nov, setNov] = useState({ code: '', name: '' });
  const [editId, setEditId] = useState<string | null>(null);
  const [editData, setEditData] = useState<any>({});

  const nalozi = () => {
    setLoading(true);
    api.get(`/sifranti/projekti/${companyId}`).then(r => { setPr(r.data); setLoading(false); }).catch(() => { setPr([]); setLoading(false); });
  };
  useEffect(() => { nalozi(); }, [companyId]);

  const dodaj = async () => {
    if (!nov.code || !nov.name) { toast.error('Šifra in naziv sta obvezna'); return; }
    try {
      await api.post(`/sifranti/projekti/${companyId}`, nov);
      toast.success('Projekt dodan!');
      setNov({ code: '', name: '' });
      setShowDodaj(false);
      nalozi();
    } catch (e: any) { toast.error(e.response?.data?.detail || 'Napaka'); }
  };

  const prevzemiIzSm = async () => {
    if (!window.confirm('Prevzamem šifre iz stroškovnih mest? Obstoječi projekti ostanejo.')) return;
    try {
      const r = await api.post(`/sifranti/projekti/${companyId}/prevzemi-sm`);
      toast.success(`Prevzeto: ${r.data.vstavljeni} novih, ${r.data.ze_obstaja} že obstoječih`);
      nalozi();
    } catch (e: any) { toast.error(e.response?.data?.detail || 'Napaka'); }
  };

  const shrani = async (id: string) => {
    try {
      await api.put(`/sifranti/projekti/${companyId}/${id}`, editData);
      toast.success('Shranjeno!');
      setPr(prev => prev.map(s => (s.id === id ? { ...s, ...editData } : s)));
      setEditId(null);
    } catch (e: any) { toast.error(e.response?.data?.detail || 'Napaka'); }
  };

  const izbrisi = async (s: any) => {
    if (!window.confirm(`Izbrišem projekt ${s.code} - ${s.name}?`)) return;
    try {
      await api.delete(`/sifranti/projekti/${companyId}/${s.id}`);
      toast.success('Izbrisano');
      setPr(prev => prev.filter(x => x.id !== s.id));
    } catch (e: any) { toast.error(e.response?.data?.detail || 'Napaka'); }
  };

  return (
    <div className={cardCls}>
      <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between gap-3">
        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Projekti</span>
        <span className="text-xs text-gray-400 flex-1">{pr.length} projektov · v VOD izvozu polje Analitika6</span>
        <button onClick={prevzemiIzSm} className="flex items-center gap-1 px-3 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-xl text-sm transition">
          Prevzemi iz SM
        </button>
        <button onClick={() => setShowDodaj(!showDodaj)} className={addBtnCls}>
          <Plus className="w-4 h-4" />Dodaj
        </button>
      </div>

      {showDodaj && (
        <div className="px-4 py-4 border-b border-gray-200 dark:border-gray-800 bg-blue-50 dark:bg-blue-950 flex items-end gap-2">
          <div>
            <label className="text-xs text-gray-500 block mb-1">Šifra *</label>
            <input value={nov.code} onChange={e => setNov(n => ({ ...n, code: e.target.value }))} placeholder="1"
              className="w-24 px-2 py-1.5 text-xs bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg outline-none focus:border-blue-500" />
          </div>
          <div className="flex-1">
            <label className="text-xs text-gray-500 block mb-1">Naziv *</label>
            <input value={nov.name} onChange={e => setNov(n => ({ ...n, name: e.target.value }))} placeholder="Naziv projekta"
              className="w-full px-2 py-1.5 text-xs bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg outline-none focus:border-blue-500" />
          </div>
          <button onClick={dodaj} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-xl">Shrani</button>
          <button onClick={() => setShowDodaj(false)} className="px-4 py-2 text-gray-500 hover:text-gray-700 text-sm">Prekliči</button>
        </div>
      )}

      <div className="overflow-x-auto max-h-[calc(100vh_-_340px)] overflow-y-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-800 sticky top-0">
            <tr>
              <th className={thCls + ' w-24'}>Šifra</th>
              <th className={thCls}>Naziv</th>
              <th className="px-4 py-3 text-center text-xs text-gray-500 font-medium w-20">Uredi</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
            {loading ? (
              <tr><td colSpan={3} className="px-4 py-8 text-center text-gray-400 text-sm">Nalaganje...</td></tr>
            ) : pr.length === 0 ? (
              <tr><td colSpan={3} className="px-4 py-8 text-center text-gray-400 text-sm">Ni projektov. Dodajte prvega ali prevzemite šifre iz SM.</td></tr>
            ) : pr.map(s => (
              <tr key={s.id} className="hover:bg-gray-50 dark:hover:bg-gray-800 transition">
                <td className="px-4 py-2 font-mono font-bold text-blue-500">
                  {editId === s.id ? (
                    <input value={editData.code || ''} onChange={e => setEditData((d: any) => ({ ...d, code: e.target.value }))} className="w-20 px-2 py-1 text-xs bg-gray-800 border border-blue-500 rounded outline-none text-white" />
                  ) : s.code}
                </td>
                <td className="px-4 py-2 text-gray-700 dark:text-gray-300">
                  {editId === s.id ? (
                    <input value={editData.name || ''} onChange={e => setEditData((d: any) => ({ ...d, name: e.target.value }))} className={inputCls} />
                  ) : s.name}
                </td>
                <td className="px-4 py-2 text-center">
                  {editId === s.id ? (
                    <div className="flex justify-center gap-1">
                      <button onClick={() => shrani(s.id)} className="p-1 text-green-500 hover:text-green-400"><Check className="w-4 h-4" /></button>
                      <button onClick={() => setEditId(null)} className="p-1 text-gray-400 hover:text-gray-300"><X className="w-4 h-4" /></button>
                    </div>
                  ) : (
                    <div className="flex justify-center gap-1">
                      <button onClick={() => { setEditId(s.id); setEditData({ code: s.code, name: s.name }); }}
                        className="p-1 text-gray-400 hover:text-blue-400 transition"><Edit2 className="w-4 h-4" /></button>
                      <button onClick={() => izbrisi(s)} className="p-1 text-gray-400 hover:text-red-400 transition"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ============================ VRSTE KNJIGE DDV ============================ */
const VK_FLAGS: { k: string; l: string }[] = [
  { k: 'uvoz', l: 'Uvoz' },
  { k: 'znotraj_skupnosti', l: 'EU (znotraj skupnosti)' },
  { k: 'os', l: 'Osnovna sredstva' },
  { k: 'nepremicnine', l: 'Nepremičnine' },
  { k: 'popravek', l: 'Popravek' },
];

function SifrantVrsteKnjige({ companyId }: { companyId: string }) {
  const [vrste, setVrste] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showDodaj, setShowDodaj] = useState(false);
  const [nov, setNov] = useState<any>({ sifra: '', naziv: '', uvoz: false, znotraj_skupnosti: false, os: false, nepremicnine: false, popravek: false });
  const [editId, setEditId] = useState<number | null>(null);
  const [editData, setEditData] = useState<any>({});

  const nalozi = () => {
    setLoading(true);
    api.get(`/sifranti/vrste-knjige/${companyId}`).then(r => { setVrste(r.data); setLoading(false); }).catch(() => { setVrste([]); setLoading(false); });
  };
  useEffect(() => { nalozi(); }, [companyId]);

  const dodaj = async () => {
    if (!String(nov.sifra).trim() || !nov.naziv) { toast.error('Šifra in naziv sta obvezna'); return; }
    try {
      await api.post(`/sifranti/vrste-knjige/${companyId}`, nov);
      toast.success('Vrsta knjige dodana!');
      setNov({ sifra: '', naziv: '', uvoz: false, znotraj_skupnosti: false, os: false, nepremicnine: false, popravek: false });
      setShowDodaj(false);
      nalozi();
    } catch (e: any) { toast.error(e.response?.data?.detail || 'Napaka'); }
  };

  const shrani = async (id: number) => {
    try {
      await api.put(`/sifranti/vrste-knjige/${companyId}/${id}`, editData);
      toast.success('Shranjeno!');
      setVrste(prev => prev.map(v => (v.id === id ? { ...v, ...editData } : v)));
      setEditId(null);
    } catch (e: any) { toast.error(e.response?.data?.detail || 'Napaka'); }
  };

  const izbrisi = async (v: any) => {
    if (!window.confirm(`Izbrišem vrsto knjige ${v.sifra} - ${v.naziv}?`)) return;
    try {
      await api.delete(`/sifranti/vrste-knjige/${companyId}/${v.id}`);
      toast.success('Izbrisano');
      setVrste(prev => prev.filter(x => x.id !== v.id));
    } catch (e: any) { toast.error(e.response?.data?.detail || 'Napaka'); }
  };

  return (
    <div className={cardCls}>
      <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between gap-3">
        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Vrste knjige DDV</span>
        <span className="text-xs text-gray-400 flex-1">{vrste.length} vrst</span>
        <button onClick={() => setShowDodaj(!showDodaj)} className={addBtnCls}>
          <Plus className="w-4 h-4" />Dodaj
        </button>
      </div>

      {showDodaj && (
        <div className="px-4 py-4 border-b border-gray-200 dark:border-gray-800 bg-blue-50 dark:bg-blue-950">
          <div className="flex items-end gap-2 mb-3">
            <div>
              <label className="text-xs text-gray-500 block mb-1">Šifra *</label>
              <input value={nov.sifra} onChange={e => setNov((n: any) => ({ ...n, sifra: e.target.value }))} placeholder="1"
                className="w-24 px-2 py-1.5 text-xs bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg outline-none focus:border-blue-500" />
            </div>
            <div className="flex-1">
              <label className="text-xs text-gray-500 block mb-1">Naziv *</label>
              <input value={nov.naziv} onChange={e => setNov((n: any) => ({ ...n, naziv: e.target.value }))} placeholder="npr. Knjiga prejetih računov"
                className="w-full px-2 py-1.5 text-xs bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg outline-none focus:border-blue-500" />
            </div>
          </div>
          <div className="flex flex-wrap gap-3 mb-3">
            {VK_FLAGS.map(f => (
              <label key={f.k} className="flex items-center gap-1 text-xs text-gray-600 dark:text-gray-400">
                <input type="checkbox" checked={!!nov[f.k]} onChange={e => setNov((n: any) => ({ ...n, [f.k]: e.target.checked }))} className="accent-blue-500" />
                {f.l}
              </label>
            ))}
          </div>
          <div className="flex gap-2">
            <button onClick={dodaj} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-xl">Shrani</button>
            <button onClick={() => setShowDodaj(false)} className="px-4 py-2 text-gray-500 hover:text-gray-700 text-sm">Prekliči</button>
          </div>
        </div>
      )}

      <div className="overflow-x-auto max-h-[calc(100vh_-_340px)] overflow-y-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-800 sticky top-0">
            <tr>
              <th className={thCls + ' w-20'}>Šifra</th>
              <th className={thCls}>Naziv</th>
              <th className={thCls}>Lastnosti</th>
              <th className="px-4 py-3 text-center text-xs text-gray-500 font-medium w-20">Uredi</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
            {loading ? (
              <tr><td colSpan={4} className="px-4 py-8 text-center text-gray-400 text-sm">Nalaganje...</td></tr>
            ) : vrste.length === 0 ? (
              <tr><td colSpan={4} className="px-4 py-8 text-center text-gray-400 text-sm">Ni vrst knjige. Dodajte prvo.</td></tr>
            ) : vrste.map(v => {
              const ed = editId === v.id;
              return (
                <tr key={v.id} className="hover:bg-gray-50 dark:hover:bg-gray-800 transition">
                  <td className="px-4 py-2 font-mono font-bold text-blue-500">
                    {ed ? (
                      <input value={editData.sifra ?? ''} onChange={e => setEditData((d: any) => ({ ...d, sifra: e.target.value }))} className="w-16 px-2 py-1 text-xs bg-gray-800 border border-blue-500 rounded outline-none text-white" />
                    ) : v.sifra}
                  </td>
                  <td className="px-4 py-2 text-gray-700 dark:text-gray-300">
                    {ed ? (
                      <input value={editData.naziv || ''} onChange={e => setEditData((d: any) => ({ ...d, naziv: e.target.value }))} className={inputCls} />
                    ) : v.naziv}
                  </td>
                  <td className="px-4 py-2">
                    {ed ? (
                      <div className="flex flex-wrap gap-2">
                        {VK_FLAGS.map(f => (
                          <label key={f.k} className="flex items-center gap-1 text-xs text-gray-500" title={f.l}>
                            <input type="checkbox" checked={!!editData[f.k]} onChange={e => setEditData((d: any) => ({ ...d, [f.k]: e.target.checked }))} className="accent-blue-500" />
                            {f.l.split(' ')[0]}
                        </label>
                      ))}
                      </div>
                    ) : (
                      <div className="flex flex-wrap gap-1">
                        {VK_FLAGS.filter(f => v[f.k]).map(f => (
                          <span key={f.k} className="bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 px-1.5 py-0.5 rounded text-xs">{f.l.split(' ')[0]}</span>
                        ))}
                        {!VK_FLAGS.some(f => v[f.k]) && <span className="text-gray-300 text-xs">—</span>}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-2 text-center">
                    {ed ? (
                      <div className="flex justify-center gap-1">
                        <button onClick={() => shrani(v.id)} className="p-1 text-green-500 hover:text-green-400"><Check className="w-4 h-4" /></button>
                        <button onClick={() => setEditId(null)} className="p-1 text-gray-400 hover:text-gray-300"><X className="w-4 h-4" /></button>
                      </div>
                    ) : (
                      <div className="flex justify-center gap-1">
                        <button onClick={() => { setEditId(v.id); setEditData({ sifra: v.sifra, naziv: v.naziv, uvoz: !!v.uvoz, znotraj_skupnosti: !!v.znotraj_skupnosti, os: !!v.os, nepremicnine: !!v.nepremicnine, popravek: !!v.popravek }); }}
                          className="p-1 text-gray-400 hover:text-blue-400 transition"><Edit2 className="w-4 h-4" /></button>
                        <button onClick={() => izbrisi(v)} className="p-1 text-gray-400 hover:text-red-400 transition"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}


// ===== Besede na konte (chip / drag-drop / urejanje) =====
function _barvaBesede(s: string): { bg: string; br: string } {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) % 360;
  return { bg: `hsl(${h}, 75%, 90%)`, br: `hsl(${h}, 45%, 72%)` };
}

function _krajsaj(s: string, n: number) {
  const t = String(s || '');
  return t.length > n ? t.slice(0, n) + '…' : t;
}

function PartnerPicker({ companyId, initialTax, initialLabel, onChange }: {
  companyId: string; initialTax?: string; initialLabel?: string; onChange: (tax: string, label: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [rez, setRez] = useState<any[]>([]);
  const [tax, setTax] = useState(initialTax || '');
  const [label, setLabel] = useState(initialLabel || '');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const qq = q.trim();
    if (qq.length < 2) { setRez([]); return; }
    const t = setTimeout(() => {
      api.get(`/sifranti/partnerji/${companyId}?limit=12&q=${encodeURIComponent(qq)}`)
        .then(r => setRez(r.data || [])).catch(() => setRez([]));
    }, 250);
    return () => clearTimeout(t);
  }, [q, companyId]);

  useEffect(() => {
    const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) { setOpen(false); setQ(''); } };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const izberi = (p: any) => {
    const t = p.tax_number || p.davcna || '';
    const ime = p.name || p.naziv || '';
    setTax(t); setLabel(ime); setOpen(false); setQ('');
    onChange(t, ime);
  };
  const pocisti = (e: React.MouseEvent) => { e.stopPropagation(); setTax(''); setLabel(''); setQ(''); onChange('', ''); };

  const prikaz = label ? _krajsaj(label, 24) : (tax ? '@' + tax : 'Izberi partnerja');
  const jePrazen = !label && !tax;

  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-sm hover:border-blue-400 transition">
        <span className={clsx('truncate', jePrazen && 'text-gray-400')}>{prikaz}</span>
        <span className="flex items-center gap-1 flex-shrink-0">
          {!jePrazen && (
            <span onClick={pocisti} title="Počisti" className="text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></span>
          )}
          <span className="text-gray-400 text-xs">▾</span>
        </span>
      </button>
      {open && (
        <div className="absolute left-0 right-0 top-11 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl z-30">
          <div className="p-2 border-b border-gray-100 dark:border-gray-700">
            <input autoFocus value={q} onChange={e => setQ(e.target.value)}
              placeholder="išči partnerja po imenu…"
              className="w-full px-2 py-1.5 bg-gray-50 dark:bg-gray-900 text-sm rounded outline-none" />
          </div>
          <div className="max-h-48 overflow-y-auto">
            {rez.length === 0 ? (
              <div className="px-3 py-3 text-xs text-gray-400">{q.trim().length < 2 ? 'Vtipkaj vsaj 2 znaka…' : 'Ni zadetkov.'}</div>
            ) : rez.map((p: any) => (
              <button key={p.id || (p.tax_number || p.davcna)} onClick={() => izberi(p)}
                className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 dark:hover:bg-blue-950/30 flex justify-between gap-2">
                <span className="truncate">{p.name || p.naziv}</span>
                <span className="font-mono text-xs text-gray-400">{p.tax_number || p.davcna}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function BesedaChip({ p, onClick, onDelete, onDragStart }: {
  p: any; onClick: () => void; onDelete: () => void; onDragStart: (e: React.DragEvent) => void;
}) {
  const c = _barvaBesede(p.beseda || '');
  const partnerPrikaz = p.partner_tax_number
    ? (p.partner_naziv ? _krajsaj(p.partner_naziv, 10) : '@' + p.partner_tax_number)
    : '';
  return (
    <span draggable onDragStart={onDragStart} onClick={onClick}
      style={{ backgroundColor: c.bg, borderColor: c.br, color: '#111827' }}
      className="inline-flex items-center gap-1.5 pl-3 pr-2 py-1.5 rounded-lg text-sm font-bold border cursor-pointer hover:brightness-95 active:cursor-grabbing select-none">
      <span>{p.beseda}</span>
      {p.ddv_kategorija && ( /* [PATCH ir4a-ui-sifrant v1] */
        <span className="text-[10px] font-semibold opacity-60 uppercase">{p.ddv_kategorija === 'dom_obdavcen' ? 'dom' : p.ddv_kategorija === 'eu_oproscen' ? 'EU' : 'opr'}</span>
      )}
      {p.vloga === 'saldakonto' && ( /* [PATCH besede-vloga-ui v1] pravilo doloca konto partnerja za cel racun */
        <span className="text-[10px] font-semibold uppercase px-1 rounded bg-black/10" title="Pravilo določa konto partnerja za celoten račun">{p.smer === 'izdani' ? 'kupec' : 'dobav.'}</span>
      )}
      {p.izvor === 'rocno' && (
        <span className="text-[11px] opacity-60" title="Ročno vneseno pravilo — ima prednost pred učnimi vzorci">✎</span>
      )}
      {partnerPrikaz && (
        <span className="text-xs font-semibold opacity-70" title={p.partner_naziv || p.partner_tax_number}>{partnerPrikaz}</span>
      )}
      <button onClick={(e) => { e.stopPropagation(); onDelete(); }} title="Odstrani"
        className="opacity-50 hover:opacity-100 leading-none text-base px-0.5">×</button>
    </span>
  );
}

function BesedaModal({ companyId, konti, koncniKonti, initial, onClose, onSaved, smer = 'prejeti' }: {
  companyId: string; konti: any[]; koncniKonti: any[]; initial: any; onClose: () => void; onSaved: () => void; smer?: string;
}) {
  const jeEdit = !!initial?.id;
  const [kategorija, setKategorija] = useState(initial?.ddv_kategorija || 'dom_obdavcen'); // [PATCH ir4a-ui-sifrant v1]
  const [beseda, setBeseda] = useState(initial?.beseda || '');
  const [konto, setKonto] = useState(initial?.konto || '');
  const [partnerTax, setPartnerTax] = useState(initial?.partner_tax_number || '');
  const [vloga, setVloga] = useState(initial?.vloga || (String(initial?.konto || '').startsWith(smer === 'izdani' ? '12' : '22') ? 'saldakonto' : 'postavka')); // [PATCH besede-vloga-ui v1]
  const [vlogaRocno, setVlogaRocno] = useState(false); // [PATCH besede-vloga-ui v2] uporabnik je vlogo izbral sam
  const [saving, setSaving] = useState(false);
  const nazivKonta = (code: string) => { const k = (konti || []).find((x: any) => x.code === code); return k ? k.name : ''; };

  const shrani = async () => {
    if (!beseda.trim()) { toast.error('Vnesite besedo'); return; }
    if (!konto.trim()) { toast.error('Vnesite konto'); return; }
    setSaving(true);
    try {
      const body: any = { beseda: beseda.trim(), konto: konto.trim(), partner_tax_number: (partnerTax || '').trim() || null, vloga }; // [PATCH besede-vloga-ui v1]
      if (smer === 'izdani') { body.ddv_kategorija = kategorija; if (!jeEdit) body.smer = 'izdani'; } // [PATCH ir4a-ui-sifrant v1]
      if (jeEdit) await api.put(`/sifranti/keywords/${companyId}/${initial.id}`, body);
      else await api.post(`/sifranti/keywords/${companyId}`, body);
      onSaved();
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Napaka pri shranjevanju');
    } finally { setSaving(false); }
  };
  const izbrisi = async () => {
    if (!jeEdit) return;
    try { await api.delete(`/sifranti/keywords/${companyId}/${initial.id}`); onSaved(); }
    catch { toast.error('Napaka pri brisanju'); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()}
        className="bg-white dark:bg-gray-900 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 w-full max-w-md p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-lg">{jeEdit ? 'Uredi pravilo' : 'Novo pravilo'}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Beseda</label>
            <input autoFocus value={beseda} onChange={e => setBeseda(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') shrani(); }}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-sm outline-none focus:border-blue-500" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Konto (končni, ≥4 mesta)</label>
            <input value={konto} onChange={e => { setKonto(e.target.value); if (!jeEdit && !vlogaRocno) setVloga(String(e.target.value || '').startsWith(smer === 'izdani' ? '12' : '22') ? 'saldakonto' : 'postavka'); } /* [PATCH besede-vloga-ui v2] */} list="kw-konti-modal"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-sm font-mono outline-none focus:border-blue-500" />
            <datalist id="kw-konti-modal">
              {koncniKonti.map((k: any) => <option key={k.code} value={k.code}>{k.code} — {k.name}</option>)}
            </datalist>
            {konto && nazivKonta(konto) && <p className="text-[11px] text-gray-400 mt-0.5">{nazivKonta(konto)}</p>}
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Pravilo določa</label>
            <select value={vloga} onChange={e => { setVloga(e.target.value); setVlogaRocno(true); } /* [PATCH besede-vloga-ui v1] + [PATCH besede-vloga-ui v2] */}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-sm outline-none focus:border-blue-500">
              <option value="postavka">{smer === 'izdani' ? 'Konto prihodka (postavka)' : 'Konto stroška (postavka)'}</option>
              <option value="saldakonto">{smer === 'izdani' ? 'Konto kupca (celoten račun)' : 'Konto dobavitelja (celoten račun)'}</option>
            </select>
            <p className="text-[11px] text-gray-400 mt-0.5">{vloga === 'saldakonto'
              ? 'Beseda se išče v postavkah in v nazivu partnerja; na ta konto gre celotna obveznost/terjatev.'
              : 'Beseda se išče na začetku besed v opisu postavke; na ta konto gre znesek postavke.'}</p>
          </div>
          {smer === 'izdani' && vloga !== 'saldakonto' && ( /* [PATCH ir4a-ui-sifrant v1] vrsta prometa (DDV) */
            <div>
              <label className="block text-xs text-gray-500 mb-1">Vrsta prometa (DDV)</label>
              <select value={kategorija} onChange={e => setKategorija(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-sm outline-none focus:border-blue-500">
                <option value="dom_obdavcen">domači obdavčen</option>
                <option value="eu_oproscen">EU oproščen (46. člen)</option>
                <option value="oproscen">oproščen</option>
              </select>
            </div>
          )}
          <div>
            <label className="block text-xs text-gray-500 mb-1">Partner (neobvezno — iz šifranta)</label>
            <PartnerPicker companyId={companyId}
              initialTax={initial?.partner_tax_number || ''} initialLabel={initial?.partner_naziv || ''}
              onChange={(tax) => setPartnerTax(tax)} />
            <p className="text-[11px] text-gray-400 mt-0.5">Če izbereš partnerja, pravilo velja le zanj; brez partnerja za vse.</p>
          </div>
        </div>
        <div className="flex items-center justify-between mt-5">
          {jeEdit ? (
            <button onClick={izbrisi}
              className="px-3 py-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30 rounded-lg text-sm flex items-center gap-1">
              <Trash2 className="w-4 h-4" /> Izbriši
            </button>
          ) : <span />}
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 text-gray-600 dark:text-gray-300 text-sm rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800">Prekliči</button>
            <button onClick={shrani} disabled={saving}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white text-sm font-semibold rounded-lg">Shrani</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function SifrantKeywords({ companyId }: { companyId: string }) {
  const [pravila, setPravila] = useState<any[]>([]);
  const [konti, setKonti] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [dragId, setDragId] = useState<number | null>(null);
  const [dropKonto, setDropKonto] = useState<string>('');
  const [modal, setModal] = useState<any | null>(null);
  const [smerTab, setSmerTab] = useState<'prejeti' | 'izdani'>(smerAplikacije(preberiAplikacijo()) === 'izdani_racun' ? 'izdani' : 'prejeti'); // [PATCH ir4a-ui-sifrant v1] // [PATCH aplikacije-fe v3] privzeto smer aplikacije

  const nalozi = () => {
    setLoading(true);
    api.get(`/sifranti/keywords/${companyId}?smer=${smerTab}`) // [PATCH ir4a-ui-sifrant v1]
      .then(r => { setPravila(r.data || []); setLoading(false); })
      .catch(() => { setPravila([]); setLoading(false); });
  };
  useEffect(() => {
    nalozi();
    api.get(`/sifranti/kontni-plan/${companyId}?surovo=true`)
      .then(r => setKonti(r.data || [])).catch(() => setKonti([]));
  }, [companyId]);
  useEffect(() => { nalozi(); }, [smerTab]); // [PATCH ir4a-ui-sifrant v1]

  const koncniKonti = useMemo(
    () => (konti || []).filter((k: any) => String(k.code || '').length >= 4),
    [konti]
  );
  const nazivKonta = (code: string) => { const k = (konti || []).find((x: any) => x.code === code); return k ? k.name : ''; };

  const skupine = useMemo(() => {
    const m: Record<string, any[]> = {};
    for (const p of pravila) (m[p.konto] = m[p.konto] || []).push(p);
    return Object.keys(m).sort().map(konto => ({
      konto, naziv: m[konto][0]?.konto_naziv || nazivKonta(konto), besede: m[konto],
    }));
  }, [pravila, konti]);

  const izbrisi = async (id: number) => {
    try { await api.delete(`/sifranti/keywords/${companyId}/${id}`); setPravila(prev => prev.filter(p => p.id !== id)); }
    catch { toast.error('Napaka pri brisanju'); }
  };
  const prestavi = async (konto: string) => {
    setDropKonto('');
    const id = dragId; setDragId(null);
    if (id == null) return;
    const p = pravila.find(x => x.id === id);
    if (!p || p.konto === konto) return;
    try { await api.put(`/sifranti/keywords/${companyId}/${id}`, { konto }); nalozi(); }
    catch (e: any) { toast.error(e?.response?.data?.detail || 'Napaka pri premikanju'); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div className="flex items-center gap-2">{/* [PATCH ir4a-ui-sifrant v1] preklop smeri */}{/* [PATCH aplikacije-fe v4] v aplikaciji je smer fiksna (Prodaja=izdani, Nabava=prejeti) */}
          {smerAplikacije(preberiAplikacijo()) ? (
            <span className="px-3 py-1.5 rounded-lg text-sm font-semibold border bg-blue-600 text-white border-blue-600">{smerTab === 'izdani' ? 'Izdani' : 'Prejeti'}</span>
          ) : (<>
          <button onClick={() => setSmerTab('prejeti')}
            className={clsx('px-3 py-1.5 rounded-lg text-sm font-semibold border transition',
              smerTab === 'prejeti' ? 'bg-blue-600 text-white border-blue-600' : 'border-gray-300 dark:border-gray-600 text-gray-500 hover:border-blue-400')}>Prejeti</button>
          <button onClick={() => setSmerTab('izdani')}
            className={clsx('px-3 py-1.5 rounded-lg text-sm font-semibold border transition',
              smerTab === 'izdani' ? 'bg-blue-600 text-white border-blue-600' : 'border-gray-300 dark:border-gray-600 text-gray-500 hover:border-blue-400')}>Izdani</button>
          </>)}{/* [PATCH aplikacije-fe v4] */}
        </div>
        <p className="text-sm text-gray-500">
          Klikni besedo za urejanje, povleci jo na drug konto, ali dodaj novo s „+”.
        </p>
        <button onClick={() => setModal({ konto: '', beseda: '', partner_tax_number: '' })}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white text-sm font-semibold rounded-lg flex items-center gap-2">
          <Plus className="w-4 h-4" /> Novo pravilo
        </button>
      </div>

      {loading ? (
        <div className="text-center py-10 text-gray-400">Nalagam…</div>
      ) : skupine.length === 0 ? (
        <div className="text-center py-10 text-gray-400">Še ni pravil. Dodaj prvo z „Novo pravilo”.</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {skupine.map(s => (
            <div key={s.konto}
              onDragOver={e => { e.preventDefault(); setDropKonto(s.konto); }}
              onDragLeave={() => setDropKonto(d => d === s.konto ? '' : d)}
              onDrop={e => { e.preventDefault(); prestavi(s.konto); }}
              className={clsx('border rounded-lg p-3 transition',
                dropKonto === s.konto
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-950/30 ring-1 ring-blue-400'
                  : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900')}>
              <div className="flex items-baseline gap-2 mb-2">
                <span className="font-mono font-bold text-blue-600">{s.konto}</span>
                <span className="text-sm text-gray-500 truncate">{s.naziv || '—'}</span>
              </div>
              <div className="flex flex-wrap gap-2 items-center">
                {s.besede.map((p: any) => (
                  <BesedaChip key={p.id} p={p}
                    onClick={() => setModal({ id: p.id, konto: p.konto, beseda: p.beseda, partner_tax_number: p.partner_tax_number, partner_naziv: p.partner_naziv, ddv_kategorija: p.ddv_kategorija, vloga: p.vloga })}
                    onDelete={() => izbrisi(p.id)}
                    onDragStart={e => { setDragId(p.id); e.dataTransfer.effectAllowed = 'move'; }} />
                ))}
                <button onClick={() => setModal({ konto: s.konto, beseda: '', partner_tax_number: '' })}
                  title="Dodaj besedo"
                  className="w-7 h-7 flex items-center justify-center rounded-md border border-dashed border-gray-400 text-gray-400 hover:border-blue-500 hover:text-blue-500 transition">
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {modal && (
        <BesedaModal companyId={companyId} konti={konti} koncniKonti={koncniKonti} smer={smerTab}
          initial={modal} onClose={() => setModal(null)}
          onSaved={() => { setModal(null); nalozi(); }} />
      )}
    </div>
  );
}

